import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import isEqual from 'lodash-es/isEqual';

import { FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import { listItemMsg } from '@/shared/components/FormattedListValue';

import { dynamicSort } from '@/shared/utils/dynamicSort';
import DayMonthPicker from '@/shared/components/input/DayMonthPicker';

import DatePicker from '@/shared/components/input/DatePicker';
import NumberInput from '@/shared/components/input/NumberInput';
import {
  actions,
  createEmptyRegistration,
  executeAction,
  isRegistrationValid,
} from '@/shared/components/registration/registrationHelpers';
import ResourceTypeRadioButtons from '@/shared/components/registration/partials/ResourceTypeRadioButtons';
import RegistrationComponent from '@/shared/components/registration/RegistrationComponent';
import guid, { guid1 } from '@/shared//utils/guid';
import {
  isBeforeTomorrow,
  validateDateRange,
  validateEndDate,
} from '@/shared/utils/validationHelpers';
import UserSearch from '@/shared/components/input/UserSearch';
import messages from '../messages';

const registrationOptions = {
  showDate: false,
  showReferenceYear: false,
  showRelation: false,
  showAssignments: false,
  showTasks: false,
  showFromUntil: false,
  showPrice: false,
  showDiscount: false,
  showInForfait: false,
  showTotalAmount: false,
  showInteraction: false,
  crmTypes: [3], //Only customers are allowed for planned registrations
};

class CreatePlannedRegistrationsModal extends Component {
  constructor(props) {
    super(props);
    const { assignmentTemplateId } = props;
    this.state = {
      schedules: [],
      schedule: null,
      dayMonthDate: { day: 1, month: 0 },
      inForfait: null,
      model: {
        scheduleId: null,
        startDate: moment
          .utc()
          .startOf('day')
          .add(1, 'days'),
        endDate: null,
        day: 1,
        month: 1,
      },
      registration: createEmptyRegistration({
        invoiceStreamId: guid.empty,
        type: 1,
        source: 2,
        quantity: 1,
        userId: guid1,
        assignmentTemplateId,
      }),
      startDateFocused: null,
      endDateFocused: null,
      showValidation: false,
    };
  }

  retrieveSchedules = () =>
    api.base.get(`${config.appUrls.api}/api/PlannedRegistration/Schedules`).then(({ data }) => {
      if (data) {
        const { intl } = this.props;
        const schedules = data
          .map(x => ({
            ...x,
            label: intl.formatMessage(listItemMsg('plannedregistrationschedule', x.id, x.name)),
          }))
          .sort(dynamicSort('label'));
        const schedule = schedules.find(x => x.numberOfLines === 1); // Select yearly schedule as default
        this.setState(({ model }) => ({
          schedules,
          schedule,
          model: { ...model, scheduleId: schedule ? schedule.id : null },
        }));
      }
    });

  componentDidMount() {
    this.retrieveSchedules();
  }

  onScheduleChange = e => {
    const { schedules } = this.state;
    const id = e.target.value || null;
    const schedule = schedules && id ? schedules.find(x => x.id === id) : null;
    this.setState(({ model }) => ({ schedule, model: { ...model, scheduleId: id } }));
  };
  onDayMonthChange = dayMonthDate => {
    this.setState(({ model }) => {
      if (!dayMonthDate) return { dayMonthDate, model: { ...model, day: null } };
      const { day, month } = dayMonthDate;
      return { dayMonthDate, model: { ...model, day, month: month + 1 } };
    });
  };
  updateModel = values => {
    this.setState(({ model }) => ({ model: { ...model, ...(values || {}) } }));
  };

  isFormInvalid = () => {
    const { model, schedule, registration } = this.state;
    if (!model || !schedule) return true;
    if (!model.scheduleId) return true;
    if (schedule.numberOfLines === 1 && (!model.day || !model.month)) return true;
    if (schedule.numberOfLines > 1 && !model.day) return true;
    if (!model.startDate || !moment(model.startDate).isValid()) return true;
    if (model.endDate && !moment(model.endDate).isValid()) return true;
    if (!validateDateRange(model.startDate, model.endDate, true)) return true;
    if (!isRegistrationValid(registration, registrationOptions)) return true;
    return false;
  };
  doRegistrationAction = (action, value) => {
    this.setState(({ registration }) => {
      const newValue = executeAction(registration, action, value);
      if (isEqual(newValue, registration)) return {};
      return { registration: newValue };
    });
  };
  userChanged = user => {
    this.setState(({ registration }) => ({ registration: { ...registration, userId: user?.id } }));
  };
  inForfaitChanged = e => {
    let inForfait = null;
    if (e.target.value === 'true') inForfait = true;
    if (e.target.value === 'false') inForfait = false;

    this.setState({ inForfait });
  };
  onSave = () => {
    if (this.isFormInvalid()) return;
    const { onSave } = this.props;
    const { model, registration, schedule, inForfait } = this.state;
    const data = { ...model, template: { ...registration, inForfait } };
    if (schedule.numberOfLines > 1) data.month = null;
    return onSave(data);
  };
  showValidation = () => this.setState({ showValidation: true });

  render() {
    const { show, onClose, intl } = this.props;
    const {
      schedules,
      schedule,
      dayMonthDate,
      model,
      registration,
      startDateFocused,
      endDateFocused,
      showValidation,
      users,
      inForfait,
    } = this.state;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.AddPlannedRegistrations)}
        show={show}
        onClose={onClose}
        disabled={showValidation && this.isFormInvalid()}
        onHoverSave={this.showValidation}
        onSave={this.onSave}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4" htmlFor="schedule">
              <FormattedMessage {...messages.Schedule} />
            </label>
            <div
              className={classNames('col-sm-7', {
                fieldError: showValidation && !model.scheduleId,
              })}
            >
              {schedules && (
                <select
                  onChange={this.onScheduleChange}
                  name="schedule"
                  className="form-control"
                  value={model.scheduleId || ''}
                >
                  {schedules.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {schedule && schedule.numberOfLines === 1 && (
            <div className="form-group">
              <label htmlFor="dayMonthPicker" className="col-sm-4 control-label">
                <FormattedMessage {...messages.DayAndMonth} />
              </label>
              <div
                className={classNames('col-sm-7', {
                  fieldError: showValidation && (!model.day || !model.month),
                })}
              >
                <DayMonthPicker
                  placeholder={intl.formatMessage(messages.DayAndMonth)}
                  value={dayMonthDate}
                  onChange={this.onDayMonthChange}
                  id="dayMonthPicker"
                  required
                  outputType="object"
                  block
                  small
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          {schedule && schedule.numberOfLines > 1 && (
            <div className="form-group">
              <label htmlFor="day" className="col-sm-4 control-label">
                <FormattedMessage {...messages.DayOfTheMonth} />
              </label>
              <div className={classNames('col-sm-7', { fieldError: showValidation && !model.day })}>
                <NumberInput
                  id="day"
                  min={1}
                  max={31}
                  step="1"
                  value={model.day}
                  onChange={value => this.updateModel({ day: value })}
                  className="form-control"
                  placeholder={intl.formatMessage(messages.Day)}
                  noDecimals
                />
              </div>
            </div>
          )}

          <div className={classNames('form-group', { 'has-error': !model.startDate })}>
            <label className="col-sm-4 control-label" htmlFor="startDate">
              <FormattedMessage {...messages.StartDate} />
            </label>
            <div className="col-sm-7">
              <DatePicker
                placeholder={intl.formatMessage(messages.StartDate)}
                date={model.startDate}
                onDateChange={date => this.updateModel({ startDate: date })}
                focused={startDateFocused}
                onFocusChange={({ focused }) => this.setState({ startDateFocused: focused })}
                id="startDate"
                required
                block
                small
                isOutsideRange={isBeforeTomorrow}
              />
            </div>
          </div>
          <div
            className={classNames('form-group', {
              'has-error': !validateEndDate(model.endDate, model.startDate),
            })}
          >
            <label className="col-sm-4 control-label" htmlFor="stopDate">
              <FormattedMessage {...messages.EndDate} />
            </label>
            <div className="col-sm-7">
              <DatePicker
                placeholder={intl.formatMessage(messages.EndDate)}
                date={model.endDate}
                onDateChange={date => this.updateModel({ endDate: date })}
                focused={endDateFocused}
                onFocusChange={({ focused }) => this.setState({ endDateFocused: focused })}
                id="endDate"
                block
                small
                isOutsideRange={isBeforeTomorrow}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-4 control-label" htmlFor="user">
              <FormattedMessage {...messages.User} />
            </label>
            <div className="col-sm-7">
              <UserSearch onChange={this.userChanged} value={registration.userId} withSystemUser />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-4 control-label" htmlFor="inForfait">
              <FormattedMessage {...messages.InForfait} />
            </label>
            <div className="col-sm-7">
              <select className="form-control" value={inForfait} onChange={this.inForfaitChanged}>
                <option value="">
                  {intl.formatMessage(messages.DetermineWhenCreatingRegistration)}
                </option>
                <option value={false}>
                  {intl.formatMessage(messages.ExcludedFromFixedAmountAdvance)}
                </option>
                <option value={true}>
                  {intl.formatMessage(messages.IncludedInFixedAmountAdvance)}
                </option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-4 control-label">
              <FormattedMessage {...messages.ResourceType} />
            </label>
            <div className="col-sm-7">
              <ResourceTypeRadioButtons
                value={registration.type}
                onChange={value => this.doRegistrationAction(actions.resourceTypeChanged, value)}
              />
            </div>
          </div>
          <RegistrationComponent
            registration={registration}
            onAction={this.doRegistrationAction}
            options={registrationOptions}
            showValidation={showValidation}
          />
        </div>
      </OkCancelModal>
    );
  }
}

CreatePlannedRegistrationsModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  assignmentTemplateId: PropTypes.string,

  intl: intlShape.isRequired,
};
CreatePlannedRegistrationsModal.defaultProps = {
  show: false,
  assignmentTemplateId: undefined,
};
export default CreatePlannedRegistrationsModal;
